import type { IntlShape } from 'react-intl';

import {
  getAuditLogChangeCategoryMessageId,
  isAuditLogChangeCategory,
} from './changeCategories';

const MESSAGE_WITH_ENTITY_NAME_ID = 'auditLogs.messageWithEntityName';
const DIVISION_LABEL_ID = 'auditLogs.divisionLabel';
const MESSAGE_WITH_DIVISION_ID = 'auditLogs.messageWithDivision';
const MESSAGE_WITH_ENTITY_NAME_AND_DIVISION_ID = 'auditLogs.messageWithEntityNameAndDivision';

export type GetAuditLogMessageOptions = {
  entityName?: string | null;
  includeEntityName?: boolean;
  divisionName?: string | null;
  /** Compact list of changed field labels (without the "Changed:" prefix). */
  changeFieldsSummary?: string | null;
};

const formatWithValues = (
  intl: IntlShape,
  messageId: string,
  values: Record<string, string>,
  fallback: string,
): string => {
  if (!intl.messages[messageId]) {
    return fallback;
  }

  return intl.formatMessage({ id: messageId }, values);
};

const formatDivisionLabel = (intl: IntlShape, divisionName: string): string => {
  if (intl.messages[DIVISION_LABEL_ID]) {
    return intl.formatMessage({ id: DIVISION_LABEL_ID }, { name: divisionName });
  }

  if (intl.messages['general.division']) {
    return `${intl.formatMessage({ id: 'general.division' })}: ${divisionName}`;
  }

  return `Division: ${divisionName}`;
};

/**
 * Resolves the displayed audit log message.
 * Prefers changeCategory i18n; falls back to description for legacy/unknown codes.
 * On the org-wide list, appends entity name and division when available.
 * When change fields are provided, appends a compact "Changed: …" summary.
 */
export const getAuditLogMessage = (
  intl: IntlShape,
  changeCategory: string | undefined,
  description: string,
  options: GetAuditLogMessageOptions = {},
): string => {
  const { entityName, includeEntityName = true, divisionName, changeFieldsSummary } = options;
  let baseMessage = description;

  if (changeCategory && isAuditLogChangeCategory(changeCategory)) {
    const messageId = getAuditLogChangeCategoryMessageId(changeCategory);
    if (intl.messages[messageId]) {
      baseMessage = intl.formatMessage({ id: messageId });
    }
  }

  if (changeFieldsSummary) {
    baseMessage = `${baseMessage}: ${changeFieldsSummary}`;
  }

  if (!includeEntityName) {
    return baseMessage;
  }

  const name = typeof entityName === 'string' ? entityName.trim() : '';
  const rawDivision = typeof divisionName === 'string' ? divisionName.trim() : '';
  const division = rawDivision ? formatDivisionLabel(intl, rawDivision) : '';

  if (name && division) {
    return formatWithValues(
      intl,
      MESSAGE_WITH_ENTITY_NAME_AND_DIVISION_ID,
      { message: baseMessage, name, division },
      `${baseMessage} · ${name} · ${division}`,
    );
  }

  if (name) {
    return formatWithValues(
      intl,
      MESSAGE_WITH_ENTITY_NAME_ID,
      { message: baseMessage, name },
      `${baseMessage} · ${name}`,
    );
  }

  if (division) {
    return formatWithValues(
      intl,
      MESSAGE_WITH_DIVISION_ID,
      { message: baseMessage, division },
      `${baseMessage} · ${division}`,
    );
  }

  return baseMessage;
};

export const getAuditLogEntityName = (log: {
  entityName?: string;
  extraKeys?: Record<string, unknown>;
}): string => {
  if (typeof log.entityName === 'string' && log.entityName.trim()) {
    return log.entityName.trim();
  }

  const extraName =
    log.extraKeys?.entityName ?? log.extraKeys?.triggerName ?? log.extraKeys?.scheduleName;
  return typeof extraName === 'string' ? extraName.trim() : '';
};
